'use client'

import { useState, useRef } from 'react'
import UIFlex from '@components/ui/UIFlex'
import { pxToRem } from '@ui/style/muiTheme'
import { theme } from '@ui/style'
import PillButton from '@components/button/PillButton'
import Popover from '@components/common/Popover'
import useClickOutside from '@components/common/Popover/useClickOutisde'
import { Box } from '@mui/joy'
import { JournalistResponseVo } from '@schemas/non-auth'
import UIText from '@components/ui/UIText'
import AuthorItem from './AuthorItem'

export interface AuthorListProps {
	journalist?: Array<JournalistResponseVo>
	isDark?: boolean
}

const AuthorList = ({ journalist, isDark }: AuthorListProps) => {
	const [isPopoverOpen, setIsPopoverOpen] = useState(false)
	const popoverRef = useRef(null)
	const count = journalist?.length ?? 0

	const openPopover = () => {
		setIsPopoverOpen(true)
	}

	const closePopover = () => {
		setIsPopoverOpen(false)
	}

	useClickOutside(popoverRef, closePopover)

	return (
		<UIFlex
			flexDirection="row"
			alignItems="center"
			flexWrap="wrap"
			sx={{
				'&>*:not(:first-child)': {
					marginLeft: pxToRem(16),
				},
				[theme.breakpoints.up('xs')]: {
					'&>.author-item:nth-child(n+2)': { display: 'none', marginLeft: 0 },
				},
				[theme.breakpoints.up('lg')]: {
					'&>.author-item:nth-child(n+2)': { display: 'flex', marginLeft: pxToRem(16) },
					'&>.author-item:nth-child(n+4)': { display: 'none', marginLeft: 0 },
				},
				[theme.breakpoints.up('xl')]: {
					'&>.author-item:nth-child(n+4)': { display: 'flex', marginLeft: pxToRem(16) },
					'&>.author-item:nth-child(n+6)': { display: 'none', marginLeft: 0 },
				},
			}}
		>
			{journalist?.map((item) => (
				<AuthorItem
					key={`journalist-${item.journalistIdx}`}
					src={item.journalistProfileImgUrl ?? ''}
					name={item.journalistName ?? ''}
					position={item.journalistPosition ?? ''}
					href={`/journalist/${item.journalistIdx}`}
					isDark={isDark}
				/>
			))}
			<Box
				position="relative"
				display={{
					xs: count <= 1 ? 'none' : 'block',
					lg: count <= 3 ? 'none' : 'block',
					xl: count <= 5 ? 'none' : 'block',
				}}
			>
				<PillButton
					size="md"
					btncolor={isDark ? 'dark' : 'light'}
					onClick={openPopover}
					sx={{ height: pxToRem(32) }}
				>
					<UIText
						component="span"
						sx={{
							fontSize: 'inherit',
							fontWeight: 'inherit',
							lineHeight: 'inheirt',
							color: 'inherit',
							userSelect: 'none',
						}}
					>
						외
						<UIText
							component="span"
							sx={{
								fontSize: 'inherit',
								fontWeight: 'inherit',
								lineHeight: 'inheirt',
								color: 'inherit',
								'&::before': {
									content: `" ${count - 1}"`,
									[theme.breakpoints.up('lg')]: {
										content: `" ${count - 3}"`,
									},
									[theme.breakpoints.up('xl')]: {
										content: `" ${count - 5}"`,
									},
								},
							}}
						/>
						명
					</UIText>
				</PillButton>
				<Popover
					ref={popoverRef}
					open={isPopoverOpen}
					onClose={closePopover}
					placement="bottom-start"
					hideHeader
					sx={{
						'.popover-body': {
							padding: pxToRem(16),
						},
					}}
				>
					<UIFlex
						sx={{
							'&>*:not(:last-child)': {
								marginBottom: pxToRem(16),
							},
							'&>.author-item:not(:nth-child(n+2))': {
								display: 'none',
							},
							[theme.breakpoints.up('lg')]: {
								'&>.author-item:not(:nth-child(n+4))': {
									display: 'none',
								},
							},
							[theme.breakpoints.up('xl')]: {
								'&>.author-item:not(:nth-child(n+6))': {
									display: 'none',
								},
							},
						}}
					>
						{journalist?.map((item) => (
							<AuthorItem
								key={`popover-journalist-${item.journalistIdx}`}
								src={item.journalistProfileImgUrl ?? ''}
								name={item.journalistName ?? ''}
								position={item.journalistPosition ?? ''}
								href={`/journalist/${item.journalistIdx}`}
							/>
						))}
					</UIFlex>
				</Popover>
			</Box>
		</UIFlex>
	)
}

export default AuthorList
